/**
 * QuoteCard Component
 * 310x310 card with abstract background image and centered affirmation text.
 * Rounded corners: 40px top-left, 40px bottom-right.
 * On hover: shows dark overlay with hamburger menu (Edit/Delete options).
 */

import { MoreVertical, Pencil, Trash2 } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { cn } from "@/lib"

/**
 * Placeholder gradient backgrounds for quote cards.
 * In production, these would be actual image URLs stored in a CDN.
 * Keys follow the "abstract-NN" pattern; we cycle through gradients via modulo.
 */
const GRADIENTS = [
  "from-amber-900/40 to-stone-900/40",
  "from-teal-900/40 to-slate-900/40",
  "from-indigo-900/40 to-gray-900/40",
  "from-orange-900/40 to-neutral-900/40",
  "from-emerald-900/40 to-zinc-900/40",
  "from-rose-900/40 to-stone-900/40",
]

function getGradient(backgroundImageKey: string): string {
  const index = Number.parseInt(backgroundImageKey.replace("abstract-", ""), 10) - 1
  return GRADIENTS[Math.abs(index) % GRADIENTS.length] || GRADIENTS[0]
}

function isImageUrl(key: string): boolean {
  return key.startsWith("http://") || key.startsWith("https://")
}

interface QuoteCardProps {
  text: string
  backgroundImageKey: string
  className?: string
  showMenu?: boolean
  onEdit?: () => void
  onDelete?: () => void
}

export function QuoteCard({
  text,
  backgroundImageKey,
  className,
  showMenu = true,
  onEdit,
  onDelete,
}: QuoteCardProps) {
  const [isHovered, setIsHovered] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const hasImageUrl = isImageUrl(backgroundImageKey)
  const gradient = hasImageUrl ? "" : getGradient(backgroundImageKey)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }

    if (isMenuOpen) {
      document.addEventListener("click", handleClickOutside)
      return () => document.removeEventListener("click", handleClickOutside)
    }
  }, [isMenuOpen])

  const handleMenuToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsMenuOpen(prev => !prev)
  }

  return (
    <div
      className={cn(
        "relative w-[310px] h-[310px] shrink-0 overflow-hidden",
        "rounded-tl-[40px] rounded-br-[40px] bg-my-rituality-bg",
        className,
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false)
        setIsMenuOpen(false)
      }}
    >
      {/* Background: real image or gradient */}
      {hasImageUrl ? (
        <img
          src={backgroundImageKey}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div className={cn("absolute inset-0 bg-gradient-to-br opacity-40", gradient)} />
      )}

      {/* Dark overlay - always visible at 40%, stronger at 60% when hovered with menu */}
      <div
        className={cn(
          "absolute inset-0 transition-opacity duration-200 z-20",
          isHovered && showMenu && "bg-[rgb(0,0,0,0.4)]",
        )}
      />

      {/* Centered text */}
      <div className="absolute inset-0 flex items-center justify-center p-8">
        <p className="text-2xl font-semibold text-white text-center uppercase leading-[1.15]">
          {text}
        </p>
      </div>

      {/* Hamburger menu button */}
      {showMenu && (
        <div ref={menuRef} className="absolute top-3 right-3 z-30">
          <button
            type="button"
            onClick={handleMenuToggle}
            className={cn(
              "p-2.5 rounded-md transition-all duration-200 border-gradient-glass cursor-pointer",
              isHovered ? "opacity-100" : "opacity-0",
            )}
          >
            <MoreVertical className="w-5 h-5 text-white" />
          </button>

          {/* Dropdown menu */}
          {isMenuOpen && (
            <div className="absolute top-full right-0 mt-2 w-[140px] rounded-[10px] bg-my-rituality-bg border border-[rgb(255,255,255,0.1)] overflow-hidden">
              <button
                type="button"
                className="flex items-center gap-2 w-full px-3 py-2.5 text-sm text-primary hover:bg-white/5 transition-colors"
                onClick={e => {
                  e.stopPropagation()
                  onEdit?.()
                }}
              >
                <Pencil className="w-4 h-4" />
                <span>Edit</span>
              </button>
              <button
                type="button"
                className="flex items-center gap-2 w-full px-3 py-2.5 text-sm text-red-400 hover:bg-white/5 transition-colors"
                onClick={e => {
                  e.stopPropagation()
                  onDelete?.()
                }}
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
